//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PojoMap } from 'pojo-maps';
import { PojoSet } from 'pojo-sets';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import {
  ITikTokVideoAutomaticPostConfig,
  UniversalScheduledPostConfigType,
} from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import { isTikTokVideoAutomaticPostConfig } from '@apis/universal-posts/config/universalScheduledPostsConfigUtils';
import {
  createDraftPosts,
  deleteDraftPosts,
  updateDraftPost,
  getDraftPosts,
} from '@apis/universal-posts/draft-posts/universalDraftPostsApi';
import { ICreateDraftPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import {
  createScheduledPosts,
  deleteScheduledPosts,
  getScheduledPost,
  updateScheduledPost,
} from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApi';
import { ICreateUpdateScheduledPost } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import { getStandardPostDetails } from '@apis/universal-posts/universalPostsUtils';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import Cuid from '@models/Cuid';
import DraftId from '@models/DraftId';
import { getMediaMetadata, IMlsMediaItem } from '@models/IMlsMediaItem';
import { PostDetailsPostType } from '@models/PostDetailsPostType';
import PostableId from '@models/PostableId';
import PostableStatus from '@models/PostableStatus';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getScheduleMediaMap } from '@redux/schedule-media/getScheduleMediaMap';
import {
  getMediaItemByCuid,
  updateMediaItems,
} from '@redux/schedule-media/scheduleMediaSlice';
import { getClientId } from '@redux/selectors/getClientId';
import {
  createDefaultCaption,
  getIsThumbnailOffsetInvalid,
  getIsVideoDurationInvalid,
} from '@tiktok/redux/schedule/post-details/tiktokPostDetailsUtils';
import {
  getVideoAutomaticCurrentCaption,
  getVideoAutomaticCurrentCuid,
  getVideoAutomaticCurrentDisableComment,
  getVideoAutomaticCurrentDisableDuet,
  getVideoAutomaticCurrentDisableStitch,
  getVideoAutomaticCurrentScheduledTime,
  getVideoAutomaticCurrentThumbnailOffsetInMs,
  getVideoAutomaticDraftId,
  getVideoAutomaticIsSavingDraftPost,
  getVideoAutomaticIsSchedulingPost,
  getVideoAutomaticPostableId,
} from '@tiktok/redux/schedule/post-details/video-automatic/selectors/getTikTokPostDetailsVideoAutomaticDetails';
import {
  CAPTION_LENGTH_LIMIT,
  fallbackComparablePostDetails,
  IComparableTikTokVideoAutomaticPostDetails,
  IStaticPostDetails,
  ITikTokVideoAutomaticPostDetailsInfoModal,
  ITikTokVideoAutomaticPostDetailsState,
  TIKTOK_MIN_DIMENSION_PX,
  TikTokVideoAutomaticValidationError,
} from '@tiktok/redux/schedule/post-details/video-automatic/tiktokVideoAutomaticPostDetailsModel';
import { getTikTokUserId } from '@tiktok/redux/user/selectors/getTikTokUser';
import {
  addSwapDraftToPostSuccessToast,
  addSwapDraftToPostFailureToast,
  addSwapPostToDraftFailureToast,
  addSwapPostToDraftSuccessToast,
} from '@universal-scheduling/redux/drafts/toasts/universalDraftsSwapToast';
import { fetchDraftById } from '@universal-scheduling/redux/drafts/universalDraftsSlice';
import { isDefined, isUndefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

import { RoutableStateName } from 'app/top-level-states';

const initialPostDetailsState: IComparableTikTokVideoAutomaticPostDetails = {
  caption: '',
  cuid: undefined,
  disableComment: false,
  disableDuet: false,
  disableStitch: false,
  scheduledTime: undefined,
  thumbnailOffsetInMs: undefined,
};

const initialInfoModalState: ITikTokVideoAutomaticPostDetailsInfoModal = {
  isOpen: false,
  contents: '',
  primaryButtonText: '',
  onCloseCallback: () => {
    return;
  },
  title: '',
};

const initialState: ITikTokVideoAutomaticPostDetailsState = {
  draftId: undefined,
  postableId: undefined,
  postType: PostDetailsPostType.TikTokVideoAutomatic,
  status: undefined,

  initialPostDetails: initialPostDetailsState,
  currentPostDetails: initialPostDetailsState,

  validationErrors: PojoSet.empty(),

  schedulingEntryPoint: undefined,

  postDetailsInfoModal: initialInfoModalState,
  isTikTokThumbnailSelectionModalOpen: false,

  errors: {
    deletePostError: undefined,
    fetchCuidsError: undefined,
    saveDraftPostError: undefined,
    schedulePostError: undefined,
    setInitialPostDetailsError: undefined,
  },
  flags: {
    hasSetInitialPostDetails: false,
    isDeletingPost: false,
    isFetchingCuid: false,
    isSavingDraftPost: false,
    isSchedulingPost: false,
  },
};

export const { actions, reducer } = createSlice({
  name: SliceNames.TikTokVideoAutomaticPostDetails,
  initialState,
  reducers: {
    setInitialPostDetails(
      state,
      {
        payload: { staticPostDetails, comparablePostDetails },
      }: PayloadAction<{
        staticPostDetails: IStaticPostDetails;
        comparablePostDetails: IComparableTikTokVideoAutomaticPostDetails;
      }>,
    ) {
      state.draftId = staticPostDetails.draftId;
      state.postableId = staticPostDetails.postableId;
      state.postType = staticPostDetails.postType;
      state.status = staticPostDetails.status;
      state.initialPostDetails = comparablePostDetails;
      state.currentPostDetails = comparablePostDetails;
      state.flags.hasSetInitialPostDetails = true;
    },
    setSelectedCuid(state, { payload: cuid }: PayloadAction<Cuid>) {
      state.currentPostDetails.cuid = cuid;
    },
    removeSelectedCuid(state) {
      // Reset the media related validation errors
      const validationErrors = PojoSet.difference(
        state.validationErrors,
        PojoSet.from([
          TikTokVideoAutomaticValidationError.InvalidVideoDimensions,
          TikTokVideoAutomaticValidationError.InvalidVideoDuration,
        ]),
      );

      state.currentPostDetails.cuid = undefined;
      state.validationErrors = validationErrors;
    },
    setCaption(state, { payload: caption }: PayloadAction<string>) {
      // Reset the CaptionTooLong validation error
      const validationErrors = PojoSet.remove(
        state.validationErrors,
        TikTokVideoAutomaticValidationError.CaptionTooLong,
      );

      state.currentPostDetails.caption = caption;
      state.validationErrors = validationErrors;
    },
    setPostTime(state, { payload: postTime }: PayloadAction<Date>) {
      // Reset the scheduled time related validation errors
      const validationErrors = PojoSet.difference(
        state.validationErrors,
        PojoSet.from([
          TikTokVideoAutomaticValidationError.NoScheduledTime,
          TikTokVideoAutomaticValidationError.ScheduledTimeInPast,
        ]),
      );

      state.currentPostDetails.scheduledTime = postTime;
      state.validationErrors = validationErrors;
    },
    setValidationErrors(
      state,
      {
        payload: validationErrors,
      }: PayloadAction<PojoSet<TikTokVideoAutomaticValidationError>>,
    ) {
      state.validationErrors = validationErrors;
    },
    setDisableComment(
      state,
      { payload: shouldDisable }: PayloadAction<boolean>,
    ) {
      state.currentPostDetails.disableComment = shouldDisable;
    },
    setDisableDuet(state, { payload: shouldDisable }: PayloadAction<boolean>) {
      state.currentPostDetails.disableDuet = shouldDisable;
    },
    setDisableStitch(
      state,
      { payload: shouldDisable }: PayloadAction<boolean>,
    ) {
      state.currentPostDetails.disableStitch = shouldDisable;
    },
    setSchedulingEntryPoint(
      state,
      { payload: entryPoint }: PayloadAction<RoutableStateName>,
    ) {
      state.schedulingEntryPoint = entryPoint;
    },
    setThumbnailOffsetInMs(
      state,
      { payload: thumbnailOffsetInMs }: PayloadAction<number>,
    ) {
      const validationErrors = PojoSet.remove(
        state.validationErrors,
        TikTokVideoAutomaticValidationError.InvalidThumbnailOffset,
      );

      state.currentPostDetails.thumbnailOffsetInMs = thumbnailOffsetInMs;
      state.validationErrors = validationErrors;
      state.isTikTokThumbnailSelectionModalOpen = false;
    },

    requestCuidMedia(state) {
      // Reset the NoMedia validation error
      const validationErrors = PojoSet.remove(
        state.validationErrors,
        TikTokVideoAutomaticValidationError.NoMedia,
      );

      state.flags.isFetchingCuid = true;
      state.validationErrors = validationErrors;
    },
    receiveCuidMediaSucceeded(state) {
      state.flags.isFetchingCuid = false;
    },
    receiveCuidMediaFailed(state) {
      state.flags.isFetchingCuid = false;
    },

    requestDeletePost(state) {
      state.flags.isDeletingPost = true;
      state.errors.deletePostError = undefined;
    },
    receiveDeletePostSucceeded(state) {
      state.flags.isDeletingPost = false;
    },
    receiveDeletePostFailed(state, { payload: errMsg }: PayloadAction<string>) {
      state.flags.isDeletingPost = false;
      state.errors.deletePostError = errMsg;
    },

    requestSchedulePost(state) {
      state.flags.isSchedulingPost = true;
      state.errors.schedulePostError = undefined;
    },
    receiveSchedulePostSucceeded(state) {
      state.flags.isSchedulingPost = false;
    },
    receiveSchedulePostFailed(
      state,
      { payload: errMsg }: PayloadAction<string>,
    ) {
      state.flags.isSchedulingPost = false;
      state.errors.schedulePostError = errMsg;
    },

    requestSaveDraftPost(state) {
      state.flags.isSavingDraftPost = true;
    },
    receiveSaveDraftPostSucceeded(state) {
      state.flags.isSavingDraftPost = false;
    },
    receiveSaveDraftPostFailed(
      state,
      { payload: errMsg }: PayloadAction<string>,
    ) {
      state.flags.isSavingDraftPost = false;
      state.errors.saveDraftPostError = errMsg;
    },

    openExitModal(
      state,
      {
        payload: modalDetails,
      }: PayloadAction<
        Omit<ITikTokVideoAutomaticPostDetailsInfoModal, 'isOpen'>
      >,
    ) {
      state.postDetailsInfoModal = {
        isOpen: true,
        ...modalDetails,
      };
    },
    closeExitModal(state) {
      state.postDetailsInfoModal = initialInfoModalState;
    },

    setIsTikTokThumbnailSelectionModalOpen(
      state,
      { payload: isTikTokThumbnailSelectionModalOpen }: PayloadAction<boolean>,
    ) {
      state.isTikTokThumbnailSelectionModalOpen =
        isTikTokThumbnailSelectionModalOpen;
    },

    resetPostDetails: () => initialState,
  },
});

export const {
  closeExitModal,
  removeSelectedCuid,
  resetPostDetails,
  setCaption,
  setDisableComment,
  setDisableDuet,
  setDisableStitch,
  setPostTime,
  setSchedulingEntryPoint,
  setThumbnailOffsetInMs,
  setIsTikTokThumbnailSelectionModalOpen,
} = actions;

export const fetchAndSetSelectedCuid = createThunk(
  (item: IContentLibraryItem) => async (dispatch, getState) => {
    const state = getState();

    const clientId = getClientId(state);
    const currentCaption = getVideoAutomaticCurrentCaption(state);
    const newCuid = Cuid.build(item, clientId);

    dispatch(actions.requestCuidMedia());

    try {
      const mediaItem = await dispatch(getMediaItemByCuid(newCuid));

      // Only set a default caption if there is not an existing caption
      if (isUndefined(currentCaption)) {
        const defaultCaption = createDefaultCaption(mediaItem);

        dispatch(actions.setCaption(defaultCaption ?? ''));
      }

      dispatch(actions.receiveCuidMediaSucceeded());
    } catch (_e) {
      dispatch(actions.receiveCuidMediaFailed());
    }

    dispatch(actions.setSelectedCuid(newCuid));
  },
);

export const setupCreatePost = createThunk(
  ({ targetCuid, postTimeMs }: { targetCuid?: Cuid; postTimeMs?: number }) =>
    async (dispatch) => {
      try {
        const mediaItem = isDefined(targetCuid)
          ? await dispatch(getMediaItemByCuid(targetCuid))
          : undefined;
        const defaultCaption = createDefaultCaption(mediaItem);

        const staticPostDetails: IStaticPostDetails = {
          postType: PostDetailsPostType.TikTokVideoAutomatic,
        };

        const comparablePostDetails: IComparableTikTokVideoAutomaticPostDetails =
          {
            caption: defaultCaption ?? '',
            cuid: targetCuid,
            disableComment: false,
            disableDuet: false,
            disableStitch: false,
            scheduledTime: isDefined(postTimeMs)
              ? new Date(postTimeMs)
              : undefined,
            thumbnailOffsetInMs: undefined,
          };

        dispatch(
          actions.setInitialPostDetails({
            staticPostDetails,
            comparablePostDetails,
          }),
        );
      } catch (e) {
        const errMsg =
          getRequestErrorMessage(e) ??
          `Failed to setup a new TikTok Video Automatic post.`;
        console.error(errMsg);

        dispatch(
          actions.setInitialPostDetails({
            staticPostDetails: {
              postType: PostDetailsPostType.TikTokVideoAutomatic,
            },
            comparablePostDetails: fallbackComparablePostDetails,
          }),
        );
      }
    },
);

export const setupCopyScheduledPost = createThunk(
  (copiedScheduledPostId?: PostableId, copiedDraftId?: DraftId) =>
    async (dispatch, getState) => {
      try {
        let scheduledPost;
        if (isDefined(copiedScheduledPostId)) {
          scheduledPost = await getScheduledPost(copiedScheduledPostId);
        } else if (isDefined(copiedDraftId)) {
          scheduledPost = (await getDraftPosts([copiedDraftId]))[0];
        } else {
          throw new Error(`Cannot copy without post or draft id`);
        }

        if (!isDefined(scheduledPost)) {
          return;
        }

        const mediaItems: IMlsMediaItem[] = PojoMap.values(
          scheduledPost.mediaItems,
        );
        const videoMediaItems: IMlsMediaItem[] = mediaItems.filter(
          (mediaItem) => isDefined(mediaItem.networkItem?.videoId),
        );

        if (videoMediaItems) {
          dispatch(
            updateMediaItems(
              PojoMap.fromEntries(
                videoMediaItems.map((mediaItem) => [mediaItem.cuid, mediaItem]),
              ),
            ),
          );
        }

        const { caption } = getStandardPostDetails(scheduledPost);

        const cuid = videoMediaItems[0]?.cuid;

        const comparablePostDetails: IComparableTikTokVideoAutomaticPostDetails =
          {
            caption: caption ?? '',
            cuid,
            disableComment: false,
            disableDuet: false,
            disableStitch: false,
            scheduledTime: undefined,
            thumbnailOffsetInMs: undefined,
          };

        const staticPostDetails: IStaticPostDetails = {
          postType: PostDetailsPostType.TikTokVideoAutomatic,
        };

        dispatch(
          actions.setInitialPostDetails({
            staticPostDetails,
            comparablePostDetails,
          }),
        );
      } catch (e) {
        dispatch(
          actions.setInitialPostDetails({
            staticPostDetails: {
              postType: PostDetailsPostType.TikTokVideoAutomatic,
            },
            comparablePostDetails: fallbackComparablePostDetails,
          }),
        );
      }
    },
);

export const setupEditPost = createThunk(
  (postId: PostableId) => async (dispatch) => {
    try {
      const scheduledPost = await getScheduledPost(postId);

      if (!isTikTokVideoAutomaticPostConfig(scheduledPost.config)) {
        throw new Error('Post missing TikTok Video Automatic config');
      }

      dispatch(updateMediaItems(scheduledPost.mediaItems));

      if (scheduledPost.status === PostableStatus.Cancelled) {
        dispatch(
          actions.openExitModal({
            contents: 'The post you are trying to view has been deleted.',
            title: 'Post Deleted',
            primaryButtonText: 'Got It',
            onCloseCallback: () => {
              void navigateToState('tiktok.schedule.calendar');
            },
          }),
        );
      }

      if (
        scheduledPost.status === PostableStatus.Processing ||
        scheduledPost.status === PostableStatus.ProcessingComplete
      ) {
        dispatch(
          actions.openExitModal({
            contents:
              'The post you are trying to edit is already in the process of being posted.',
            title: 'Post In Progress',
            primaryButtonText: 'Got It',
            onCloseCallback: () => {
              void navigateToState('tiktok.schedule.calendar');
            },
          }),
        );
      }

      if (scheduledPost.status === PostableStatus.Posted) {
        dispatch(
          actions.openExitModal({
            contents:
              'The post you are trying to edit has already been posted.',
            title: 'Post Posted',
            primaryButtonText: 'Got It',
            onCloseCallback: () => {
              void navigateToState('tiktok.schedule.calendar');
            },
          }),
        );
      }

      const staticPostDetails: IStaticPostDetails = {
        postableId: scheduledPost.id,
        postType: PostDetailsPostType.TikTokVideoAutomatic,
        status: scheduledPost.status,
      };

      const comparablePostDetails: IComparableTikTokVideoAutomaticPostDetails =
        {
          caption: scheduledPost.config.caption ?? '',
          cuid: scheduledPost.config.cuids[0],
          disableComment: !!scheduledPost.config.disableComment,
          disableDuet: !!scheduledPost.config.disableDuet,
          disableStitch: !!scheduledPost.config.disableStitch,
          scheduledTime: scheduledPost.scheduledTime,
          thumbnailOffsetInMs: scheduledPost.config.thumbnailOffsetInMs,
        };

      dispatch(
        actions.setInitialPostDetails({
          staticPostDetails,
          comparablePostDetails,
        }),
      );
    } catch (e) {
      const errMsg =
        getRequestErrorMessage(e) ??
        `Failed to load existing TikTok Video Automatic post: ${postId}.`;
      console.error(errMsg);

      dispatch(
        actions.setInitialPostDetails({
          staticPostDetails: {
            postableId: postId,
            postType: PostDetailsPostType.TikTokVideoAutomatic,
          },
          comparablePostDetails: fallbackComparablePostDetails,
        }),
      );

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Unable to Load Post',
          type: ToastType.ERROR,
          message:
            'We were unable to load the existing post. Please try again later.',
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    }
  },
);

export const setupEditDraft = createThunk(
  (draftId: DraftId) => async (dispatch) => {
    try {
      const draftPost = await dispatch(fetchDraftById(draftId));

      if (!isDefined(draftPost)) {
        throw new Error(`Could not load draft post: ${draftId}`);
      }

      if (!isTikTokVideoAutomaticPostConfig(draftPost.config)) {
        throw new Error(
          `Missing TikTok Video Automatic config for draft post: ${draftId}`,
        );
      }

      const staticPostDetails: IStaticPostDetails = {
        draftId: draftPost.id,
        postType: PostDetailsPostType.TikTokVideoAutomatic,
      };

      const comparablePostDetails: IComparableTikTokVideoAutomaticPostDetails =
        {
          caption: draftPost.config.caption ?? '',
          cuid: draftPost.config.cuids[0],
          disableComment: !!draftPost.config.disableComment,
          disableDuet: !!draftPost.config.disableDuet,
          disableStitch: !!draftPost.config.disableStitch,
          scheduledTime: draftPost.scheduledTime,
          thumbnailOffsetInMs: draftPost.config.thumbnailOffsetInMs,
        };

      dispatch(
        actions.setInitialPostDetails({
          staticPostDetails,
          comparablePostDetails,
        }),
      );
    } catch (e) {
      const errMsg =
        getRequestErrorMessage(e) ??
        `Failed to load existing TikTok Video Automatic draft post: ${draftId}.`;
      console.error(errMsg);

      dispatch(
        actions.setInitialPostDetails({
          staticPostDetails: {
            postType: PostDetailsPostType.TikTokVideoAutomatic,
          },
          comparablePostDetails: fallbackComparablePostDetails,
        }),
      );

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Unable to Load Draft',
          type: ToastType.ERROR,
          message:
            'We were unable to load the existing draft. Please try again later.',
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    }
  },
);

const schedulePost = createThunk(() => async (dispatch, getState) => {
  const state = getState();
  const caption = getVideoAutomaticCurrentCaption(state);
  const cuid = getVideoAutomaticCurrentCuid(state);
  const disableComment = getVideoAutomaticCurrentDisableComment(state);
  const disableDuet = getVideoAutomaticCurrentDisableDuet(state);
  const disableStitch = getVideoAutomaticCurrentDisableStitch(state);
  const postableId = getVideoAutomaticPostableId(state);
  const postingEntityId = getTikTokUserId(state);
  const scheduledTime = getVideoAutomaticCurrentScheduledTime(state);
  const thumbnailOffsetInMs =
    getVideoAutomaticCurrentThumbnailOffsetInMs(state);

  // These are all required to schedule a TikTok Video Automatic post
  if (
    !isDefined(cuid) ||
    !isDefined(scheduledTime) ||
    !isDefined(postingEntityId)
  ) {
    return false;
  }

  const saveRequest: ICreateUpdateScheduledPost = {
    config: {
      '@type': UniversalScheduledPostConfigType.TikTokVideoAutomaticPost,
      cuids: [cuid],
      caption,
      disableComment,
      disableDuet,
      disableStitch,
      thumbnailOffsetInMs,
    },
    postingEntityId,
    scheduledTime,
  };

  try {
    dispatch(actions.requestSchedulePost());

    if (isDefined(postableId)) {
      await updateScheduledPost(postableId, saveRequest);
    } else {
      await createScheduledPosts([saveRequest]);
    }

    dispatch(actions.receiveSchedulePostSucceeded());

    return true;
  } catch (e) {
    const defaultErrorMessage = `Failed to schedule post of type: ${UniversalScheduledPostConfigType.TikTokVideoAutomaticPost}`;
    console.error(defaultErrorMessage);

    dispatch(
      actions.receiveSchedulePostFailed(
        getRequestErrorMessage(e) ?? defaultErrorMessage,
      ),
    );

    dispatch(
      toastNotificationsActions.addToastNotification({
        title: 'Unable to Schedule Post',
        type: ToastType.ERROR,
        message: 'We were unable to schedule the post. Please try again later.',
        options: {
          toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
        },
      }),
    );

    return false;
  }
});

export const validateAndSchedulePost = createThunk(() => async (dispatch) => {
  const isPostValid = dispatch(validatePostDetails());

  if (isPostValid) {
    const isSuccessfullyScheduled = await dispatch(schedulePost());

    return isSuccessfullyScheduled;
  }

  return false;
});

export const validatePostDetails = createThunk(() => (dispatch, getState) => {
  const state = getState();
  const caption = getVideoAutomaticCurrentCaption(state);
  const cuid = getVideoAutomaticCurrentCuid(state);
  const scheduleMediaMap = getScheduleMediaMap(state);
  const scheduledTime = getVideoAutomaticCurrentScheduledTime(state);
  const thumbnailOffsetInMs =
    getVideoAutomaticCurrentThumbnailOffsetInMs(state);

  const formErrors: TikTokVideoAutomaticValidationError[] = [];

  if (caption.length > CAPTION_LENGTH_LIMIT) {
    formErrors.push(TikTokVideoAutomaticValidationError.CaptionTooLong);
  }

  if (!isDefined(cuid)) {
    formErrors.push(TikTokVideoAutomaticValidationError.NoMedia);
  } else {
    const mediaItem = PojoMap.get(scheduleMediaMap, cuid);

    if (!isDefined(mediaItem)) {
      formErrors.push(TikTokVideoAutomaticValidationError.NoMedia);
      return false;
    }

    const mediaMetadata = getMediaMetadata(mediaItem);
    if (isUndefined(mediaMetadata)) {
      formErrors.push(TikTokVideoAutomaticValidationError.NoMedia);
      return false;
    }

    const videoDuration = mediaMetadata.durationMillis;

    if (getIsVideoDurationInvalid(videoDuration)) {
      formErrors.push(TikTokVideoAutomaticValidationError.InvalidVideoDuration);
    } else {
      if (
        isDefined(videoDuration) &&
        isDefined(thumbnailOffsetInMs) &&
        getIsThumbnailOffsetInvalid(videoDuration, thumbnailOffsetInMs)
      ) {
        formErrors.push(
          TikTokVideoAutomaticValidationError.InvalidThumbnailOffset,
        );
      }
    }

    if (
      mediaMetadata.width < TIKTOK_MIN_DIMENSION_PX ||
      mediaMetadata.height < TIKTOK_MIN_DIMENSION_PX
    ) {
      formErrors.push(
        TikTokVideoAutomaticValidationError.InvalidVideoDimensions,
      );
    }
  }

  if (!isDefined(scheduledTime)) {
    formErrors.push(TikTokVideoAutomaticValidationError.NoScheduledTime);
  } else if (scheduledTime < new Date()) {
    formErrors.push(TikTokVideoAutomaticValidationError.ScheduledTimeInPast);
  }

  if (formErrors.length > 0) {
    dispatch(actions.setValidationErrors(PojoSet.from(formErrors)));
  }

  return formErrors.length === 0;
});

export const deleteScheduledPost = createThunk(
  (postableId: PostableId) => async (dispatch) => {
    dispatch(actions.requestDeletePost());

    try {
      await deleteScheduledPosts([postableId]);
      dispatch(actions.receiveDeletePostSucceeded());

      return true;
    } catch (e) {
      dispatch(
        actions.receiveDeletePostFailed(
          getRequestErrorMessage(e) ??
            `Failed to delete scheduled TikTok Video Automatic post: ${postableId}`,
        ),
      );

      return false;
    }
  },
);

export const saveDraft = createThunk(() => async (dispatch, getState) => {
  const state = getState();
  const caption = getVideoAutomaticCurrentCaption(state);
  const cuid = getVideoAutomaticCurrentCuid(state);
  const disableComment = getVideoAutomaticCurrentDisableComment(state);
  const disableDuet = getVideoAutomaticCurrentDisableDuet(state);
  const disableStitch = getVideoAutomaticCurrentDisableStitch(state);
  const draftId = getVideoAutomaticDraftId(state);
  const postingEntityId = getTikTokUserId(state);
  const scheduledTime = getVideoAutomaticCurrentScheduledTime(state);
  const thumbnailOffsetInMs =
    getVideoAutomaticCurrentThumbnailOffsetInMs(state);

  // These are all required to schedule a TikTok Video Automatic post
  if (!isDefined(postingEntityId)) {
    return false;
  }

  const updateConfig: ITikTokVideoAutomaticPostConfig = {
    '@type': UniversalScheduledPostConfigType.TikTokVideoAutomaticPost,
    cuids: [...(cuid ? [cuid] : [])],
    caption,
    disableComment,
    disableDuet,
    disableStitch,
    thumbnailOffsetInMs,
  };

  try {
    dispatch(actions.requestSaveDraftPost());

    if (isDefined(draftId)) {
      await updateDraftPost(draftId, updateConfig, scheduledTime);
    } else {
      const saveRequest: ICreateDraftPost = {
        config: updateConfig,
        postingEntityId,
        scheduledTime,
      };
      await createDraftPosts([saveRequest]);
    }

    dispatch(actions.receiveSaveDraftPostSucceeded());

    return true;
  } catch (e) {
    const defaultErrorMessage = `Failed to save draft post type: ${UniversalScheduledPostConfigType.TikTokVideoAutomaticPost}`;

    console.error(defaultErrorMessage);

    dispatch(
      actions.receiveSaveDraftPostFailed(
        getRequestErrorMessage(e) ?? defaultErrorMessage,
      ),
    );

    dispatch(
      toastNotificationsActions.addToastNotification({
        title: 'Unable to Save Draft',
        type: ToastType.ERROR,
        message: 'We were unable to save your draft. Please try again later.',
        options: {
          toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
        },
      }),
    );

    return false;
  }
});

const deleteDraftPost = createThunk(
  (draftId: DraftId) =>
    async (dispatch): Promise<boolean> => {
      dispatch(actions.requestDeletePost());

      try {
        await deleteDraftPosts([draftId]);

        return true;
      } catch (e) {
        console.error(
          getRequestErrorMessage(e) ??
            `Failed to delete TikTok Video Automatic draft: ${draftId}`,
        );

        return false;
      }
    },
);

export const swapDraftToScheduledPost = createThunk(
  () =>
    async (dispatch, getState): Promise<boolean> => {
      const state = getState();
      const draftId = getVideoAutomaticDraftId(state);
      const isSchedulingPost = getVideoAutomaticIsSchedulingPost(state);
      const isSavingDraftPost = getVideoAutomaticIsSavingDraftPost(state);

      // Return early if no draftId exists which means we should be calling a different method
      if (!isDefined(draftId)) {
        return false;
      }

      // Return early if the post is already being saved / scheduled
      if (isSchedulingPost || isSavingDraftPost) {
        return false;
      }

      const didSchedulePost = await dispatch(validateAndSchedulePost());

      if (!didSchedulePost) {
        // Error handling will come from validateAndSchedulePost
        return false;
      }

      const didDeleteDraft = await dispatch(deleteDraftPost(draftId));

      if (didDeleteDraft) {
        dispatch(addSwapDraftToPostSuccessToast());
      } else {
        dispatch(addSwapDraftToPostFailureToast());
      }

      return didDeleteDraft;
    },
);

export const swapScheduledPostToDraft = createThunk(
  () =>
    async (dispatch, getState): Promise<boolean> => {
      const state = getState();
      const postableId = getVideoAutomaticPostableId(state);
      const isSchedulingPost = getVideoAutomaticIsSchedulingPost(state);
      const isSavingDraftPost = getVideoAutomaticIsSavingDraftPost(state);

      // Return early if no postableId exists which means we should be calling a different method
      if (!isDefined(postableId)) {
        return false;
      }

      // Return early if the post is already being saved / scheduled
      if (isSchedulingPost || isSavingDraftPost) {
        return false;
      }

      const didSaveDraft = await dispatch(saveDraft());

      if (!didSaveDraft) {
        // Error handling will come from saveDraft
        return false;
      }

      const didDeleteScheduledPost = await dispatch(
        deleteScheduledPost(postableId),
      );

      if (didDeleteScheduledPost) {
        dispatch(addSwapPostToDraftSuccessToast());
      } else {
        dispatch(addSwapPostToDraftFailureToast());
      }

      return didDeleteScheduledPost;
    },
);
